import * as Sentry from '@sentry/react';
import {
  CollectionChatRequestPayload,
  CollectionChatResponseBody,
  CollectionChatStreamEvent,
} from '@/features/collection-chat/types';
import { getFreshToken } from '@/utils/auth-token-provider';

type StreamCallback = (event: CollectionChatStreamEvent) => void;

const buildChatUrl = (orgId: string, collectionId: string): string => {
  const encodedOrgId = encodeURIComponent(orgId);
  const encodedCollectionId = encodeURIComponent(collectionId);
  return `/proxy/platform/collections/${encodedOrgId}/${encodedCollectionId}/chat`;
};

const buildHeaders = async (paramToken?: string | null) => {
  const freshToken = await getFreshToken();
  const token = freshToken ?? paramToken;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['X-User-Token'] = token;
  }

  return headers;
};

const extractErrorMessage = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const candidate = payload as Record<string, unknown>;
  const fields: Array<'detail' | 'message' | 'error'> = [
    'detail',
    'message',
    'error',
  ];

  for (const field of fields) {
    const value = candidate[field];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }

  return null;
};

const parseErrorResponse = async (response: Response): Promise<string> => {
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    try {
      const data = await response.json();
      return (
        data?.detail ||
        data?.message ||
        data?.error ||
        `Request failed with status ${response.status}`
      );
    } catch {
      return `Request failed with status ${response.status}`;
    }
  }

  try {
    const text = (await response.text())?.trim();
    return text || `Request failed with status ${response.status}`;
  } catch {
    return `Request failed with status ${response.status}`;
  }
};

export const processJsonlBuffer = (
  buffer: string,
  emit: (line: string) => void,
): string => {
  let remainder = buffer;
  let newlineIndex = remainder.indexOf('\n');

  while (newlineIndex !== -1) {
    const line = remainder.slice(0, newlineIndex).trim();
    remainder = remainder.slice(newlineIndex + 1);
    if (line) {
      emit(line);
    }
    newlineIndex = remainder.indexOf('\n');
  }

  return remainder;
};

const emitStreamEvent = (line: string, callback: StreamCallback): void => {
  try {
    const parsed = JSON.parse(line) as CollectionChatStreamEvent;
    if (parsed && typeof parsed.type === 'string') {
      callback(parsed);
    }
  } catch (error) {
    console.warn('Failed to parse chat stream chunk', error);
    Sentry.captureException(error, {
      extra: { context: 'parse-chat-stream-chunk', line },
    });
  }
};

interface BaseChatParams {
  orgId: string;
  collectionId: string;
  token?: string | null;
  body: CollectionChatRequestPayload;
  signal?: AbortSignal;
}

export interface StreamCollectionChatParams extends BaseChatParams {
  onEvent: StreamCallback;
}

//TODO: use apiRequest from @/utils/api-request

export const streamCollectionChat = async ({
  orgId,
  collectionId,
  token,
  body,
  signal,
  onEvent,
}: StreamCollectionChatParams): Promise<void> => {
  if (!orgId) {
    throw new Error('Organization ID is required to start a chat session.');
  }

  if (!collectionId) {
    throw new Error('Collection ID is required to start a chat session.');
  }

  const payload: CollectionChatRequestPayload = {
    ...body,
    stream: true,
  };

  const headers = await buildHeaders(token);

  let response: Response;
  try {
    response = await fetch(buildChatUrl(orgId, collectionId), {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal,
    });
  } catch (error) {
    Sentry.captureException(error, {
      extra: { context: 'stream-collection-chat-fetch', orgId, collectionId },
    });
    throw error;
  }

  if (!response.ok) {
    const errorMessage = await parseErrorResponse(response);
    const error = new Error(errorMessage);
    Sentry.captureException(error, {
      extra: {
        context: 'stream-collection-chat-response',
        status: response.status,
        orgId,
        collectionId,
      },
    });
    throw error;
  }

  if (!response.body) {
    throw new Error(
      'Streaming is not supported in this browser. Disable streaming to continue.',
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      buffer = processJsonlBuffer(buffer, (line) =>
        emitStreamEvent(line, onEvent),
      );
    }

    buffer += decoder.decode();
    buffer = processJsonlBuffer(buffer, (line) =>
      emitStreamEvent(line, onEvent),
    );

    const finalLine = buffer.trim();
    if (finalLine) {
      emitStreamEvent(finalLine, onEvent);
    }
  } finally {
    reader.releaseLock();
  }
};

export const fetchCollectionChat = async ({
  orgId,
  collectionId,
  token,
  body,
  signal,
}: BaseChatParams): Promise<CollectionChatResponseBody> => {
  if (!orgId) {
    throw new Error('Organization ID is required to start a chat session.');
  }

  if (!collectionId) {
    throw new Error('Collection ID is required to start a chat session.');
  }

  const payload: CollectionChatRequestPayload = {
    ...body,
    stream: false,
  };

  const headers = await buildHeaders(token);

  let response: Response;
  try {
    response = await fetch(buildChatUrl(orgId, collectionId), {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal,
    });
  } catch (error) {
    Sentry.captureException(error, {
      extra: { context: 'fetch-collection-chat-fetch', orgId, collectionId },
    });
    throw error;
  }

  const rawText = await response.text();
  let data: unknown = null;

  if (rawText) {
    try {
      data = JSON.parse(rawText);
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    const errorMessage =
      extractErrorMessage(data) ||
      rawText ||
      `Request failed with status ${response.status}`;
    const error = new Error(errorMessage);
    Sentry.captureException(error, {
      extra: {
        context: 'fetch-collection-chat-response',
        status: response.status,
        orgId,
        collectionId,
      },
    });
    throw error;
  }

  if (!data || typeof data !== 'object') {
    throw new Error('Chat response was empty or malformed.');
  }

  return data as CollectionChatResponseBody;
};
